import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Package2, Plus, Search, Loader2, Trash2, Headset, UserRound, Copy } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge as BadgeComponent } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { copyCredentialPassword } from '@/lib/clipboard'
import { getErrorMessage } from '@/lib/api'
import { isBranchHierarchyTier } from '@/lib/org-hierarchy-from-instance'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import {
  getTenantOwnerDisplayName,
  resolveTenantServiceInstance,
  resolveTenantServiceInstanceOwnerUserId,
} from '@/lib/org-service-instance-owner'
import {
  buildSupportLoginAttempts,
  getSupportLoginAttemptErrorMessage,
  resolveSupportLoginFailureMessage,
} from '@/lib/support-login'
import { authApi } from '@/services/auth'
import { branchesApi } from '@/services/branches'
import { orgTreeApi, type OrgTreeServiceInstance } from '@/services/org-tree'
import { useAuthStore } from '@/stores/auth-store'

const SHOW_IN_APPS_FEATURE = true

// Only include variants that exist in the database
const ALLOWED_TENANT_IN_APP_TYPES = new Set([
  'fundraising',
  'membership',
  'charity',
  'academic',
])

const normalizeInAppTypeLabel = (value: string) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (normalized === 'acadamic') {
    return 'academic'
  }
  return normalized
}

const isAllowedTenantInAppType = (value: string) => {
  const normalized = normalizeInAppTypeLabel(value)
  if (!normalized) {
    return false
  }

  return Array.from(ALLOWED_TENANT_IN_APP_TYPES).some((allowed) => normalized === allowed || normalized.includes(allowed))
}

export default function InAppsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPost = useAuthStore((state) => state.currentPost)
  const fallbackServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const [search, setSearch] = useState('')
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [formData, setFormData] = useState({ name: '', serviceVariant: '' })
  const serviceInstanceIdFromSearch = getServiceInstanceIdFromSearch(location.search)
  const currentPostServiceInstanceId = String(
    currentPost?.service_instance ?? currentPost?.service_instance_id ?? '',
  ).trim() || null
  const serviceInstanceId = serviceInstanceIdFromSearch || fallbackServiceInstanceId || currentPostServiceInstanceId || null

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ instanceId: string; name: string } | null>(null)
  const [generatedBranchCredentials, setGeneratedBranchCredentials] = useState<{
    instanceName: string
    title: string
    credentials: {
      userId?: string | number | null
      username?: string | null
      email?: string | null
      phone?: string | null
      name?: string | null
      password?: string | null
    }
    serviceInstanceId?: string | null
  } | null>(null)
  type SupportLoginStep = 'logging-out' | 'fetching-org' | 'logging-in' | 'done' | 'error'
  const [supportLoginProgress, setSupportLoginProgress] = useState<{
    step: SupportLoginStep
    error?: string
  } | null>(null)
  const queryClient = useQueryClient()
  const hasAutoOpenedAddModal = useRef(false)
  const shouldAutoOpenAddModal = SHOW_IN_APPS_FEATURE
    && ['1', 'true', 'yes'].includes(String(searchParams.get('open_add') || '').trim().toLowerCase())
  const canChangeTenantSuperAdmin = Boolean(currentPost?.capabilities?.can_change_tenant_super_admin)

  useEffect(() => {
    if (serviceInstanceIdFromSearch || !serviceInstanceId) {
      return
    }

    const next = new URLSearchParams(searchParams)
    next.set('service_instance_id', serviceInstanceId)
    setSearchParams(next, { replace: true })
  }, [searchParams, serviceInstanceId, serviceInstanceIdFromSearch, setSearchParams])

  
  const inAppsQuery = useQuery({
    queryKey: ['org-tree-in-apps', serviceInstanceId],
    queryFn: () => orgTreeApi.listInAppInstances(String(serviceInstanceId)),
    enabled: Boolean(serviceInstanceId),
  })

  // Fetch current service instance details for location info
  const currentInstanceQuery = useQuery({
    queryKey: ['service-instance-details', serviceInstanceId],
    queryFn: () => orgTreeApi.getServiceInstance(String(serviceInstanceId)),
    enabled: Boolean(serviceInstanceId),
  })

  // Fetch service variants
  const serviceVariantsQuery = useQuery({
    queryKey: ['service-variants', serviceInstanceId, currentInstanceQuery.data?.parent_instance_id],
    queryFn: async () => {
      const scopedServiceInstanceId = String(serviceInstanceId ?? '').trim()
      const scopedVariants = await orgTreeApi.listOutboundServiceVariants(scopedServiceInstanceId)
      if (scopedVariants.length > 0) {
        return scopedVariants
      }

      const parentServiceInstanceId = String(currentInstanceQuery.data?.parent_instance_id ?? '').trim()
      if (!parentServiceInstanceId || parentServiceInstanceId === scopedServiceInstanceId) {
        return scopedVariants
      }

      return orgTreeApi.listOutboundServiceVariants(parentServiceInstanceId)
    },
    enabled: Boolean(serviceInstanceId),
  })

  const visibleServiceVariants = useMemo(() => {
    return (serviceVariantsQuery.data || []).filter((variant) => isAllowedTenantInAppType(String(variant.name || '')))
  }, [serviceVariantsQuery.data])

  const tenantInstanceQuery = useQuery({
    queryKey: ['tenant-service-instance-owner', serviceInstanceId],
    queryFn: () => resolveTenantServiceInstance(String(serviceInstanceId)),
    enabled: Boolean(serviceInstanceId) && isAddModalOpen,
    staleTime: 60_000,
  })

  const tenantOwnerLabel = useMemo(() => {
    const ownerName = getTenantOwnerDisplayName(tenantInstanceQuery.data)
    const ownerUserId = String(tenantInstanceQuery.data?.owner_user_id ?? '').trim()
    if (ownerName) {
      return ownerName
    }
    if (ownerUserId) {
      return `User ${ownerUserId}`
    }
    return 'Tenant owner'
  }, [tenantInstanceQuery.data])

  // Delete in-app mutation
  const deleteInAppMutation = useMutation({
    mutationFn: (instanceId: string) => orgTreeApi.deleteServiceInstance(instanceId),
    onSuccess: () => {
      toast.success('In-App deleted successfully')
      setDeleteTarget(null)
      queryClient.invalidateQueries({ queryKey: ['org-tree-in-apps', serviceInstanceId] })
    },
    onError: (error) => {
      toast.error(`Failed to delete in-app: ${getErrorMessage(error)}`)
    },
  })

  const supportRoleMutation = useMutation({
    mutationFn: async (branchRef: string) => branchesApi.assignSupportAdmin(branchRef),
    onSuccess: async (result) => {
      toast.success('Support role credentials generated successfully')
      if (result.credentials) {
        const loginReference = String(
          result.branch?.parentInstanceId
          ?? result.branch?.instanceId
          ?? serviceInstanceId
          ?? '',
        ).trim() || null

        setGeneratedBranchCredentials({
          instanceName: String(currentInstanceQuery.data?.name || currentInstanceQuery.data?.title || 'Current Branch').trim(),
          title: 'Support Role Credentials',
          credentials: result.credentials,
          serviceInstanceId: loginReference,
        })
      }
      await queryClient.invalidateQueries({ queryKey: ['org-tree-in-apps', serviceInstanceId] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const handleLoginToSupportRole = useCallback(async () => {
    const creds = generatedBranchCredentials
    if (!creds) {
      return
    }

    const email = creds.credentials.email ?? ''
    const username = creds.credentials.username ?? ''
    const phone = creds.credentials.phone ?? ''
    const password = creds.credentials.password ?? ''
    const scopedServiceInstanceId = creds.serviceInstanceId ?? ''

    if (!password) {
      toast.error('Generated password is unavailable for support login.')
      return
    }

    if (password) {
      void navigator.clipboard.writeText(password).catch(() => undefined)
    }

    setGeneratedBranchCredentials(null)
    setSupportLoginProgress({ step: 'logging-out' })

    try {
      await authApi.logoutApiOnly()

      setSupportLoginProgress({ step: 'fetching-org' })
      await new Promise((r) => setTimeout(r, 500))

      setSupportLoginProgress({ step: 'logging-in' })

      const loginAttempts = buildSupportLoginAttempts({
        email,
        username,
        phone,
        password,
        serviceInstanceId: scopedServiceInstanceId,
      })

      let loginResult = null
      let lastError: unknown = null
      const attemptMessages: string[] = []

      for (const payload of loginAttempts) {
        try {
          loginResult = await authApi.login(payload)
          break
        } catch (attemptError) {
          lastError = attemptError
          attemptMessages.push(getSupportLoginAttemptErrorMessage(attemptError))
        }
      }

      if (!loginResult) {
        throw new Error(resolveSupportLoginFailureMessage(attemptMessages, lastError))
      }

      setSupportLoginProgress({ step: 'done' })
      await new Promise((r) => setTimeout(r, 500))

      const resolvedId = loginResult?.data?.service_instance_id || scopedServiceInstanceId
      setSupportLoginProgress(null)
      navigate(resolvedId ? `/posts?service_instance_id=${resolvedId}` : '/posts', { replace: true })
    } catch (err) {
      setSupportLoginProgress({
        step: 'error',
        error: err instanceof Error ? err.message : 'Login failed. Please try manually.',
      })
    }
  }, [generatedBranchCredentials, navigate])

  // Create in-app mutation
  const createInAppMutation = useMutation({
    mutationFn: async (payload: { name: string; serviceVariant: string }) => {
      const slugify = (value: string) => value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

      const prefixIdentifier = slugify(String(
        currentInstanceQuery.data?.identifier
        ?? currentInstanceQuery.data?.url_identifier
        ?? currentInstanceQuery.data?.instance_id
        ?? serviceInstanceId
        ?? '',
      ))
      const appNameIdentifier = slugify(payload.name) || 'in-app'
      const appIdentifier = [prefixIdentifier, appNameIdentifier].filter(Boolean).join('-') || appNameIdentifier

      const ownerUserId = await resolveTenantServiceInstanceOwnerUserId(String(serviceInstanceId ?? ''))

      const currentInstance = currentInstanceQuery.data
      const scopedInstanceIsBranch = isBranchHierarchyTier(currentInstance)
      const branchNodeId = String(
        searchParams.get('branch_node_id')
        ?? searchParams.get('branch_node')
        ?? (scopedInstanceIsBranch ? currentInstance?.primary_node : '')
        ?? '',
      ).trim()
      const parentServiceInstanceId = String(
        (scopedInstanceIsBranch ? currentInstance?.parent_instance_id : null)
        ?? serviceInstanceId
        ?? '',
      ).trim()

      const createPayload: Parameters<typeof orgTreeApi.createNodeAppInstance>[0] = {
        service_variant: payload.serviceVariant,
        name: payload.name,
        branch_ownership: '1',
        owner_type: '1',
        lvl_variant: 'in-apps',
        identifier: appIdentifier,
        url_identifier: appIdentifier,
      }

      if (ownerUserId) {
        createPayload.owner_user_id = ownerUserId
      }

      if (branchNodeId) {
        createPayload.branch_node = branchNodeId
        createPayload.service_instance = parentServiceInstanceId
      } else {
        createPayload.branch_service_instance = String(serviceInstanceId)
      }

      return orgTreeApi.createNodeAppInstance(createPayload)
    },
    onSuccess: (response) => {
      toast.success('In-App instance created successfully')
      setIsAddModalOpen(false)
      setFormData({ name: '', serviceVariant: '' })
      
      // Optimistically add the created instance to the cache if it was returned
      if (response?.data) {
        queryClient.setQueryData(
          ['org-tree-in-apps', serviceInstanceId],
          (existing: OrgTreeServiceInstance[] | undefined) => {
            if (!existing) return existing
            const newInstance: OrgTreeServiceInstance = {
              ...response.data,
              // Normalize instance_id
              instance_id: response.data.instance_id || response.data.id,
            } as OrgTreeServiceInstance
            return [...existing, newInstance]
          }
        )
      } else {
        // Fallback to refetch if no data returned
        queryClient.invalidateQueries({ queryKey: ['org-tree-in-apps', serviceInstanceId] })
      }
    },
    onError: (error) => {
      toast.error(`Failed to create in-app: ${getErrorMessage(error)}`)
    },
  })

  const handleAddClick = useCallback(() => {
    setIsAddModalOpen(true)
  }, [])

  useEffect(() => {
    if (!serviceInstanceId || hasAutoOpenedAddModal.current || !shouldAutoOpenAddModal) {
      return
    }

    hasAutoOpenedAddModal.current = true
    setIsAddModalOpen(true)
  }, [serviceInstanceId, shouldAutoOpenAddModal])

  const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      toast.error('Please enter an in-app name')
      return
    }
    
    if (!formData.serviceVariant) {
      toast.error('Please select an app type')
      return
    }

    setIsSubmitting(true)
    try {
      await createInAppMutation.mutateAsync(formData)
    } finally {
      setIsSubmitting(false)
    }
  }, [formData, createInAppMutation])

  const handleModalClose = useCallback(() => {
    if (!isSubmitting) {
      setIsAddModalOpen(false)
      setFormData({ name: '', serviceVariant: '' })
    }
  }, [isSubmitting])

  const handleViewInApp = useCallback((inApp: {
    instance_id?: string | number | null
    id?: string | number | null
    primary_node?: string | number | null
  }) => {
    const formRouteId = String(inApp.id || inApp.instance_id || '').trim()
    const targetInstanceId = String(inApp.instance_id || inApp.id || '').trim()
    if (!targetInstanceId || !formRouteId) {
      toast.error('Unable to open this in-app. Missing instance id.')
      return
    }

    const query = new URLSearchParams({
      lvl_variant: 'branches',
      unique_level_variant_id: 'branches',
      service_instance_id: String(serviceInstanceId),
      action: 'edit',
      id: targetInstanceId,
      tab: 'details',
      settingsMode: 'organization',
      returnTo: 'branch-directory',
      nodeId: String(inApp.primary_node || currentInstanceQuery.data?.primary_node || ''),
    })

    navigate(`/org/service-instance/form/${encodeURIComponent(formRouteId)}?${query.toString()}`)
  }, [currentInstanceQuery.data?.primary_node, navigate, serviceInstanceId])

  const handleChangePrimaryRole = useCallback((inApp: {
    instance_id?: string | number | null
    id?: string | number | null
    primary_node?: string | number | null
  }) => {
    handleViewInApp(inApp)
  }, [handleViewInApp])

  const handleSupportRole = useCallback(() => {
    const branchRef = String(serviceInstanceId || '').trim()
    if (!branchRef) {
      toast.error('Unable to generate support role credentials. Missing instance id.')
      return
    }
    supportRoleMutation.mutate(branchRef)
  }, [serviceInstanceId, supportRoleMutation])

  const appTypeNameByVariantKey = useMemo(() => {
    const map = new Map<string, string>()
    for (const variant of visibleServiceVariants) {
      const variantName = String(variant.name || '').trim()
      if (!variantName) {
        continue
      }

      const keys = [
        String(variant.id || '').trim(),
        String(variant.serviceId || '').trim(),
        String(variant.serviceUniqueId || '').trim(),
      ].filter(Boolean)

      for (const key of keys) {
        if (!map.has(key)) {
          map.set(key, variantName)
        }
      }
    }
    return map
  }, [visibleServiceVariants])

  const getInAppServiceVariantName = useCallback((inApp: Record<string, any>) => {
    const candidateKeys = [
      String(inApp.service_data?.variant_unique || '').trim(),
      String(inApp.service_variant || '').trim(),
      String(inApp.service_id || '').trim(),
    ].filter(Boolean)

    for (const key of candidateKeys) {
      const mapped = appTypeNameByVariantKey.get(key)
      if (mapped) {
        return mapped
      }
    }

    return String(inApp.service_variant_name || inApp.service_variant_label || inApp.service_variant_service || '-').trim() || '-'
  }, [appTypeNameByVariantKey])

  const filteredInApps = useMemo(() => {
    if (!inAppsQuery.data) return []
    if (!search.trim()) return inAppsQuery.data

    const searchLower = search.toLowerCase().trim()
    return inAppsQuery.data.filter((app) => {
      const name = String(app.name || app.title || '').toLowerCase()
      const instanceId = String(app.instance_id || '').toLowerCase()
      const owner = String(app.owner_name || '').toLowerCase()
      
      return name.includes(searchLower) || instanceId.includes(searchLower) || owner.includes(searchLower)
    })
  }, [inAppsQuery.data, search])

  if (!serviceInstanceId) {
    return (
      <div className="rounded-[28px] border border-destructive/20 bg-card p-8 text-center">
        <p className="text-base font-medium text-destructive">Missing service instance context</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Open the In-Apps from your organisation workspace to load the context properly.
        </p>
        <Button className="mt-4" variant="outline" onClick={() => navigate(withServiceInstanceId('/org/service-instance', serviceInstanceId))}>
          Back
        </Button>
      </div>
    )
  }

  if (inAppsQuery.isLoading) {
    return (
      <div className="space-y-6 pb-2">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="rounded-2xl border border-border/70 p-5">
              <Skeleton className="h-5 w-44" />
              <Skeleton className="mt-4 h-4 w-full" />
              <Skeleton className="mt-2 h-4 w-2/3" />
              <Skeleton className="mt-5 h-10 w-32" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (inAppsQuery.error) {
    return (
      <div className="rounded-[28px] border border-destructive/20 bg-card p-8 text-center">
        <p className="text-base font-medium text-destructive">Failed to load In-Apps</p>
        <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(inAppsQuery.error)}</p>
        <Button className="mt-4" variant="outline" onClick={() => inAppsQuery.refetch()}>
          Retry
        </Button>
      </div>
    )
  }

  const totalInAppsCount = inAppsQuery.data?.length ?? 0
  const hiddenByFilter = Math.max(totalInAppsCount - filteredInApps.length, 0)
  const searchActive = search.trim().length > 0

  return (
    <div className="space-y-6 pb-2">
      <Card className="rounded-[28px] border-border/80">
        <CardContent className="p-6 sm:p-8">
          <div className="space-y-3">
            <Badge variant="outline" className="w-fit rounded-md border-blue-200 bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300">
              In-Apps Management
            </Badge>
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">In-Apps Directory</h1>
              <p className="max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
                Manage service applications across your organization branches and tenants.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => navigate(withServiceInstanceId('/org/service-instance', serviceInstanceId))}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              {SHOW_IN_APPS_FEATURE ? (
                <Button onClick={handleAddClick}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add In-App
                </Button>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-[28px] border-border/80">
        <CardHeader className="gap-4 border-b border-border/70 pb-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <CardTitle className="text-lg">In-Apps Directory</CardTitle>
              <CardDescription>
                Browse all in-app service instances configured in your organization.
              </CardDescription>
            </div>

            <div className="relative min-w-[280px] max-w-full lg:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search in-apps, IDs, or owners"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          {totalInAppsCount === 0 ? (
            <Empty className="rounded-2xl border border-dashed border-border/70 bg-muted/10">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Package2 className="h-5 w-5" />
                </EmptyMedia>
                <EmptyTitle>No in-apps found</EmptyTitle>
                <EmptyDescription>
                  No in-app service instances are available for your organization yet.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : filteredInApps.length === 0 && searchActive ? (
            <div className="rounded-2xl border border-dashed border-border/70 bg-muted/10 px-4 py-8 text-center text-sm text-muted-foreground">
              No in-apps match the current search.
            </div>
          ) : (
            <div className="space-y-6">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-foreground">In-App Instances</p>
                  <p className="text-sm text-muted-foreground">
                    {filteredInApps.length} of {totalInAppsCount} visible
                    {hiddenByFilter > 0 && ` • ${hiddenByFilter} hidden by filters`}
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredInApps.map((inApp) => (
                    <Card key={String(inApp.instance_id)} className="rounded-[20px] border-border/80 overflow-hidden hover:shadow-md transition-shadow">
                      <div className="h-0.5 w-full bg-gradient-to-r from-blue-400/40 via-blue-400/15 to-transparent" />

                      <div className="p-4 space-y-4">
                        {/* ── Header ── */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 flex-col gap-1">
                            <h3 className="font-semibold text-foreground line-clamp-2">
                              {inApp.name || inApp.title || 'Unnamed In-App'}
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              {inApp.instance_id || 'N/A'}
                            </p>
                          </div>
                          <div className="shrink-0">
                            <Package2 className="h-5 w-5 text-muted-foreground" />
                          </div>
                        </div>

                        {/* ── Details Grid ── */}
                        <div className="space-y-3 border-t border-border/50 pt-3">
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                              <p className="text-muted-foreground font-medium">Branch</p>
                              <p className="text-foreground font-semibold mt-0.5 truncate">
                                {inApp.level_variant_name || inApp.tier_code || '-'}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground font-medium">App Type</p>
                              <p className="text-foreground font-semibold mt-0.5 truncate">
                                {getInAppServiceVariantName(inApp as any)}
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                              <p className="text-muted-foreground font-medium">Owner</p>
                              <p className="text-foreground font-semibold mt-0.5 truncate">
                                {inApp.owner_name || 'Not assigned'}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground font-medium">Registration Status</p>
                              <BadgeComponent 
                                variant={
                                  inApp.status === 1 || inApp.status === '1' ? 'default' :
                                  inApp.status === 0 || inApp.status === '0' ? 'secondary' :
                                  inApp.status === 3 || inApp.status === '3' ? 'destructive' :
                                  'outline'
                                }
                                className="mt-0.5"
                              >
                                {inApp.status === 1 || inApp.status === '1' ? 'Approved' :
                                 inApp.status === 0 || inApp.status === '0' ? 'Pending' :
                                 inApp.status === 2 || inApp.status === '2' ? 'Rejected' :
                                 inApp.status === 3 || inApp.status === '3' ? 'Suspended' :
                                 inApp.status === 4 || inApp.status === '4' ? 'Terminated' :
                                 'Unknown'}
                              </BadgeComponent>
                            </div>
                          </div>
                        </div>

                        {/* ── Footer ── */}
                        <div className="border-t border-border/50 pt-3 flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs h-8"
                            onClick={() => handleViewInApp(inApp)}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-8"
                            onClick={() => handleChangePrimaryRole(inApp)}
                            title="Change Primary Role"
                          >
                            <UserRound className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-8"
                            title={canChangeTenantSuperAdmin ? 'Generate support role credentials' : 'You need permission to generate support role credentials.'}
                            onClick={() => handleSupportRole()}
                            disabled={!canChangeTenantSuperAdmin || supportRoleMutation.isPending}
                          >
                            <Headset className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setDeleteTarget({
                              instanceId: String(inApp.instance_id || inApp.id || ''),
                              name: String(inApp.name || inApp.title || 'this in-app'),
                            })}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete In-App</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteInAppMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteInAppMutation.isPending}
              onClick={() => deleteTarget && deleteInAppMutation.mutate(deleteTarget.instanceId)}
            >
              {deleteInAppMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</>
              ) : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={Boolean(generatedBranchCredentials)}
        onOpenChange={(open) => {
          if (!open) {
            setGeneratedBranchCredentials(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{generatedBranchCredentials?.title || 'Generated Credentials'}</DialogTitle>
            <DialogDescription>
              {generatedBranchCredentials
                ? `Use these approved credentials for ${generatedBranchCredentials.instanceName}.`
                : 'Generated credentials are available.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-2xl border border-border/70 bg-muted/15 px-4 py-3 text-sm text-muted-foreground">
              Save the password now. It is shown only for this reset/create action.
            </div>
            <div className="rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm">
              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Name</p>
              <p className="mt-1 font-medium text-foreground">{generatedBranchCredentials?.credentials.name || '-'}</p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm">
              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Email</p>
              <p className="mt-1 break-all font-medium text-foreground">{generatedBranchCredentials?.credentials.email || '-'}</p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm">
              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Phone</p>
              <p className="mt-1 break-all font-medium text-foreground">{generatedBranchCredentials?.credentials.phone || '-'}</p>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.14em] text-emerald-700">Password</p>
                  <p className="mt-1 break-all font-semibold text-emerald-900">{generatedBranchCredentials?.credentials.password || '-'}</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-emerald-200 bg-white/80 text-emerald-900 hover:bg-white"
                  onClick={() => void copyCredentialPassword(generatedBranchCredentials?.credentials.password)}
                  disabled={!generatedBranchCredentials?.credentials.password}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy password
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleLoginToSupportRole()}
              disabled={!generatedBranchCredentials?.serviceInstanceId || !generatedBranchCredentials?.credentials.password}
            >
              Login to support role
            </Button>
            <Button
              onClick={() => {
                setGeneratedBranchCredentials(null)
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(supportLoginProgress)}
        onOpenChange={(open) => {
          if (!open && supportLoginProgress?.step === 'error') {
            setSupportLoginProgress(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-[380px]" showCloseButton={supportLoginProgress?.step === 'error'}>
          <DialogHeader>
            <DialogTitle>Switching to support role</DialogTitle>
            <DialogDescription>
              Please wait while we securely switch your session.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {(['logging-out', 'fetching-org', 'logging-in'] as const).map((step, idx) => {
              const label = {
                'logging-out': 'Logging out',
                'fetching-org': 'Fetching Org information',
                'logging-in': 'Logging in now',
              }[step]
              const order = ['logging-out', 'fetching-org', 'logging-in', 'done']
              const currentIdx = order.indexOf(supportLoginProgress?.step ?? '')
              const stepIdx = order.indexOf(step)
              const isDone = currentIdx > stepIdx
              const isActive = currentIdx === stepIdx

              return (
                <div key={step} className="flex items-center gap-3">
                  <div className={[
                    'h-6 w-6 rounded-full border flex items-center justify-center text-xs font-medium',
                    isDone
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : isActive
                        ? 'border-primary text-primary'
                        : 'border-border text-muted-foreground',
                  ].join(' ')}>
                    {isDone ? '✓' : idx + 1}
                  </div>
                  <p className={[
                    'text-sm',
                    isActive ? 'text-foreground font-medium' : 'text-muted-foreground',
                  ].join(' ')}>
                    {label}
                  </p>
                  {isActive && supportLoginProgress?.step !== 'error' && (
                    <span className="ml-auto h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  )}
                </div>
              )
            })}

            {supportLoginProgress?.step === 'error' && (
              <p className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {supportLoginProgress.error}
              </p>
            )}
          </div>

          {supportLoginProgress?.step === 'error' && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setSupportLoginProgress(null)}>
                Close
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Add In-App Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleModalClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add In-App Instance</DialogTitle>
            <DialogDescription>
              Create a new in-app service instance for your organization. Location settings will be inherited from the current instance.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleFormSubmit} className="space-y-4">
            {/* Name Input */}
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">
                In-App Name *
              </label>
              <Input
                id="name"
                placeholder="e.g., Main Store Services"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={isSubmitting}
              />
            </div>

            {/* Service Variant Select */}
            <div className="space-y-2">
              <label htmlFor="service-variant" className="text-sm font-medium">
                App Type *
              </label>
              <Select
                value={formData.serviceVariant}
                onValueChange={(value) => setFormData({ ...formData, serviceVariant: value })}
                disabled={isSubmitting || serviceVariantsQuery.isLoading}
              >
                <SelectTrigger id="service-variant">
                  <SelectValue placeholder={serviceVariantsQuery.isLoading ? 'Loading app types...' : 'Select app type'} />
                </SelectTrigger>
                <SelectContent>
                  {visibleServiceVariants.map((variant) => (
                    <SelectItem key={String(variant.id)} value={String(variant.id)}>
                      {variant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!serviceVariantsQuery.isLoading && visibleServiceVariants.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No enabled in-app variants are available for this organization yet.
                </p>
              ) : null}
            </div>

            {/* Owner */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Owner</label>
              <div className="rounded-md border border-border/70 bg-muted/20 px-3 py-2 text-sm text-foreground">
                {tenantInstanceQuery.isLoading ? 'Loading tenant owner...' : tenantOwnerLabel}
              </div>
              <p className="text-xs text-muted-foreground">
                New apps are created as Own type and use the tenant service instance owner.
              </p>
            </div>

            {/* Info about inherited settings */}
            <div className="rounded-md bg-blue-50 p-3 text-xs text-blue-700 dark:bg-blue-950/20 dark:text-blue-300">
              The following settings will be inherited from the current instance:
              <ul className="mt-1 list-inside list-disc space-y-0.5">
                <li>Country: {currentInstanceQuery.data?.country_name || 'Not set'}</li>
                <li>Timezone: {currentInstanceQuery.data?.time_zone || 'Not set'}</li>
                <li>Currency: {currentInstanceQuery.data?.currency_code || 'Not set'}</li>
              </ul>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleModalClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !formData.name.trim() || !formData.serviceVariant}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Create In-App
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
